import { useState, useEffect, useCallback } from "react";
import { History } from "lucide-react";

const AUTO_DISMISS_MS = 10_000;

interface RestoreDraftDetail {
  entries: Array<{ savedAt: string }>;
}

export const RestoredDraftIndicator: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [timestamp, setTimestamp] = useState<string | null>(null);

  const handleRestore = useCallback((e: Event) => {
    const detail = (e as CustomEvent<RestoreDraftDetail>).detail;
    const savedAt = detail?.entries?.[0]?.savedAt;
    if (savedAt) {
      const formatted = new Date(savedAt).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      setTimestamp(formatted);
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("maintenance:restore-draft", handleRestore);
    return () => window.removeEventListener("maintenance:restore-draft", handleRestore);
  }, [handleRestore]);

  // Auto-dismiss after 10s or on form submit (AC5 Task 3.4)
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    const handleConsumed = () => setVisible(false);
    window.addEventListener("maintenance:draft-consumed", handleConsumed);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("maintenance:draft-consumed", handleConsumed);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm text-amber-600 dark:text-amber-400"
    >
      <History className="h-4 w-4" />
      <span>Rascunho restaurado{timestamp ? ` de ${timestamp}` : ""}</span>
    </div>
  );
};
